import {
    Badge,
    Box,
    Flex,
    Grid,
    HStack,
    Spinner,
    Tab, TabList, TabPanel, TabPanels, Tabs,
    Text,
} from '@chakra-ui/react';
import { AccessIssueSuggestedAction, PortfolioPropertyTag } from '@acp/shared';
import { AccessIssuesWizardMachineContext } from '../machine/context';

import { useProperty } from '../../../../property';

import { AccessIssuesWizardDebugBar } from './debug-bar';
import { AccessIssuesWizardNoSolutionsWindow, AccessIssuesWizardResolvedWindow } from './resolved-window';
import { SolutionIcon } from './solution-icon';
import { AccessIssuesWizardSolutionRenderer } from '../solution-renderer';
import { AccessIssuesWizardSolutionProps } from '../types';

function TabContent({
    icon,
    children,
    isMostEffective,
}: {
    icon: React.ReactNode;
    isMostEffective: boolean;
    children: React.ReactNode;
}) {
    return (
        <HStack py={4} px={3} alignSelf="stretch" position="relative">
            { isMostEffective && <Badge position="absolute" top="-10px" left={2} fontWeight="medium" colorScheme="success">Most effective</Badge> }
            { icon }
            <Text fontWeight="medium" textAlign="start" lineHeight={1.1}>
                {children}
            </Text>
        </HStack>
    );
}

function RenderResolution({
    action,
}: {
    action: AccessIssueSuggestedAction;
}) {
    const machine = AccessIssuesWizardMachineContext.useActorRef();
    const propertyId = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.propertyId
    );

    const problemSet = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.problemSet.current!
    );

    const caseDetails = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.caseDetails!
    );

    const isResolutionCompleted = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('resolveIssueCompleted')
    );

    const entryMethod = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.entryMethods.current!
    );

    const propertyQuery = useProperty(propertyId);

    const tags = propertyQuery.data?.tags ?? [];
    const isPropertyIntegrated = tags.includes(PortfolioPropertyTag.Integrated);

    const handleAgencyAction: AccessIssuesWizardSolutionProps['onAgencyAction'] = (opt) => {
        machine.send({
            type: 'MARK_AS_AGENCY_ACTION',
            solutionLabel: action.solution.solution_label,
            notes: opt?.notes,
            date: opt?.date,
        });
    };

    if (isResolutionCompleted) {
        return <AccessIssuesWizardResolvedWindow />;
    }

    let softwareName = propertyQuery.data?.softwareName;

    let solutionDescription = isPropertyIntegrated
        ? action?.solution?.description_integrated
        : action?.solution?.description_non_integrated;

    solutionDescription = solutionDescription.replace('{PMS}', softwareName ?? '');
    return (
        <>
            <AccessIssuesWizardDebugBar
                propertyId={propertyId}
                solution={action.solution}
                onAgencyAction={handleAgencyAction}
                problemSet={problemSet}
                caseDetails={caseDetails}
                entryMethod={entryMethod}
            />
            <HStack marginBottom={2} gap={1}>
                <Text>
                    {solutionDescription}
                </Text>
            </HStack>
            <AccessIssuesWizardSolutionRenderer
                softwareName={softwareName}
                isPropertyIntegrated={isPropertyIntegrated}
                propertyId={propertyId}
                solution={action.solution}
                onAgencyAction={handleAgencyAction}
                problemSet={problemSet}
                caseDetails={caseDetails}
                entryMethod={entryMethod}
            />
        </>
    );
}

function AccessIssuesWizardResolutionRenderer() {
    const machine = AccessIssuesWizardMachineContext.useActorRef();

    const currentProblemSet = AccessIssuesWizardMachineContext.useSelector(
        // Guaranteed to be defined
        (state) => state.context.problemSet.current!
    );

    const isResolutionCompleted = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('resolveIssueCompleted')
    );

    const isResolutionPending = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('resolveIssuePending')
    );

    const tabIdx = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.selectedSolutionIdx
    );

    // Can't show any solutions in the UI if there are no suggested actions
    // We'll instead tell them to contact us as previous attempt to resolve have failed
    // And given them the option to close modal / skip the problem set
    if (currentProblemSet.suggested_actions.length === 0) {
        return <AccessIssuesWizardNoSolutionsWindow />;
    }

    return (
        <Tabs
            index={tabIdx}
            variant="enclosed-basic"
            colorScheme={isResolutionCompleted ? 'success' : 'muted'}
            width="100%"
            onChange={(idx) => machine.send({ type: 'SELECT_SOLUTION', idx })}
            lazyBehavior="keepMounted"
            isLazy
        >
            <TabList width="100%">
                {
                    currentProblemSet.suggested_actions.map(
                        (action, idx) => (
                            <Tab
                                fontSize="sm"
                                key={action.solution.solution_label}
                                isDisabled={isResolutionCompleted}
                            >
                                <TabContent
                                    icon={(
                                        <SolutionIcon
                                            solutionLabel={action.solution.solution_label}
                                            w={5}
                                            h={5}
                                        />
                                    )}
                                    isMostEffective={
                                        currentProblemSet.suggested_actions.length > 1
                                    && idx === 0
                                    }
                                >
                                    {action.solution.solution_public_name}
                                </TabContent>
                            </Tab>
                        )
                    )
                }
            </TabList>
            <TabPanels roundedBottom="md">
                {
                    currentProblemSet.suggested_actions.map(
                        (action) => (
                            <TabPanel key={action.solution.solution_label}>
                                <Grid templateColumns="1fr" templateRows="1fr">
                                    <Box gridColumn={1} gridRow={1}>
                                        <RenderResolution action={action} />
                                    </Box>
                                    {
                                        isResolutionPending && (
                                            <Flex
                                                alignItems="center"
                                                justifyContent="center"
                                                width="100%"
                                                height="100%"
                                                bgColor="whiteAlpha.800"
                                                gridColumn={1}
                                                gridRow={1}
                                                zIndex={1000}
                                            >
                                                <Spinner />
                                            </Flex>
                                        )
                                    }
                                </Grid>
                            </TabPanel>
                        )
                    )
                }
            </TabPanels>
        </Tabs>
    );
}

export { AccessIssuesWizardResolutionRenderer };
